import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Filter, MoreVertical, Trash2, Edit, CheckCircle, XCircle, ChevronDown, ChevronLeft, ChevronRight, PlusCircle, Search } from 'lucide-react';
import EditUserModal from './components/modals/EditUserModal';
import AddUserModal from './components/modals/AddUserModal';
import { useUser } from './context/UserContext';

export default function UserManagementScreen() {
  const navigate = useNavigate();
  const { user: currentUser } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ id: '', email: '', nome: '', hub: '', status: '' });
  const [activeFilters, setActiveFilters] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [showFilters, setShowFilters] = useState(false); 

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin-dev': return 'Admin (Master)';
      case 'admin-op': return 'Admin (Operações)';
      case 'operator': return 'Operador';
      default: return role;
    }
  };

  const fetchUsers = useCallback(async (pageToFetch) => {
    setLoading(true);
    setError(null);
    setOpenMenuId(null);
    try {
      const token = sessionStorage.getItem('app_token');
      if (!token) throw new Error("Token não encontrado.");

      const params = { ...activeFilters, page: pageToFetch, page_size: 30 };
      const response = await axios.get('/api/users', {
        params, headers: { 'Authorization': `Bearer ${token}` }
      });

      setUsers(response.data.data);
      setCurrentPage(response.data.page);
      setTotalPages(response.data.total_pages);

    } catch (err) {
      setError(err.response?.data?.detail || "Não foi possível carregar os usuários.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilters]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, fetchUsers]);

  const handleStatusToggle = async (userToUpdate) => {
    const newStatus = userToUpdate.status === 'ativo' ? 'desativado' : 'ativo';
    const confirmation = window.confirm(`Tem certeza que deseja ${newStatus === 'ativo' ? 'ATIVAR' : 'DESATIVAR'} o usuário ${userToUpdate.nome}?`);
    
    if (confirmation) {
      try {
        const token = sessionStorage.getItem('app_token');
        await axios.put(
          `/api/users/${userToUpdate.id}`,
          { status: newStatus },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        fetchUsers(currentPage);
      } catch (err) {
        alert(err.response?.data?.detail || 'Falha ao atualizar o status do usuário.');
      }
    }
    setOpenMenuId(null);
  };

  const handleEditClick = (userToEdit) => {
    setEditingUser(userToEdit);
    setOpenMenuId(null);
  };

  // --- FUNÇÃO DE FILTRO ATUALIZADA ---
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    // Bloqueia a entrada de valores não numéricos no campo de ID
    if (name === 'id' && !/^[0-9]*$/.test(value)) {
      return;
    }
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  // --- FIM DA ATUALIZAÇÃO ---

  const applyFilters = () => {
    setActiveFilters(Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ id: '', email: '', nome: '', hub: '', status: '' });
    setActiveFilters({});
    setCurrentPage(1);
  };

  const renderContent = () => {
    if (loading) return <div className="text-center mt-10">Carregando usuários...</div>;
    if (error) return <div className="text-center mt-10 text-red-500">{error}</div>;
    if (users.length === 0) return <div className="text-center mt-10 text-gray-500">Nenhum usuário encontrado.</div>;

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        {users.map((user, index) => {
          const canEdit = !(currentUser.role === 'admin-op' && user.role === 'admin-dev');
          return (
            <div key={user.id} className={`flex items-center p-4 ${index < users.length - 1 ? 'border-b border-gray-200' : ''}`}>
              <div className="flex-shrink-0 mr-4">
                {user.status === 'ativo'
                  ? <CheckCircle size={24} className="text-green-500" title="Ativo" />
                  : <XCircle size={24} className="text-red-500" title="Desativado" />
                }
              </div>
              <div className="flex-grow text-sm">
                <p className="font-bold text-gray-800 text-base">{user.nome} {user.sobrenome}</p>
                <p className="text-gray-600">{user.email}</p>
                <div className="text-gray-500 mt-1">
                  <p>ID: <span className="font-medium">{user.id}</span></p>
                  <p>Hub: <span className="font-medium">{user.hub || 'N/A'}</span></p>
                  <p>Role: <span className="font-medium">{getRoleDisplayName(user.role)}</span></p>
                </div>
              </div>
              <div className="relative flex-shrink-0 ml-4">
                <button onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)} className="p-2 rounded-full hover:bg-gray-100">
                  <MoreVertical size={20} />
                </button>
                {openMenuId === user.id && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border">
                    <ul className="py-1">
                      <li>
                        <button
                          onClick={() => handleEditClick(user)}
                          disabled={!canEdit}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Edit size={16} className="mr-2" /> Editar
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => handleStatusToggle(user)}
                          disabled={!canEdit}
                          className="w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {user.status === 'ativo'
                            ? <><Trash2 size={16} className="mr-2 text-red-500" /> <span className="text-red-500">Desativar</span></>
                            : <><CheckCircle size={16} className="mr-2 text-green-500" /> <span className="text-green-500">Ativar</span></>
                          }
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/home')} className="p-1">
            <ArrowLeft size={20} className="text-[#EE4D2D]" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Usuários</h1>
        </div>
        <button
          onClick={() => setIsAddModalVisible(true)}
          className="flex items-center bg-[#EE4D2D] text-white font-semibold py-1.5 px-3 rounded-md hover:bg-[#D03F2A] transition-colors text-sm"
        >
          <PlusCircle size={15} className="mr-1" />
          Novo Usuário
        </button>
      </div>

      {/* Barra de pesquisa */}
      <div className="flex flex-col mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center bg-gray-100 text-gray-700 py-1.5 px-4 rounded-full hover:bg-gray-200 transition-colors"
        >
          <Search size={17} className="mr-2" />
          <span>Filtrar...</span>
        </button>

        {showFilters && ( 
          <div className="bg-white p-4 border border-gray-200 rounded-lg mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <input type="text" inputMode="numeric" name="id" placeholder="Filtrar por ID" value={filters.id} onChange={handleFilterChange} className="p-2 border rounded-lg w-full" />
              <input type="text" name="email" placeholder="Filtrar por Email" value={filters.email} onChange={handleFilterChange} className="p-2 border rounded-lg w-full" />
              <input type="text" name="nome" placeholder="Filtrar por Nome" value={filters.nome} onChange={handleFilterChange} className="p-2 border rounded-lg w-full" />
              <input type="text" name="hub" placeholder="Filtrar por Hub" value={filters.hub} onChange={handleFilterChange} className="p-2 border rounded-lg w-full" />
              <div className="relative">
                <select name="status" value={filters.status} onChange={handleFilterChange} className="p-2 border rounded-lg w-full bg-white appearance-none pr-8 cursor-pointer">
                  <option value="">Todos os status</option>
                  <option value="ativo">Ativo</option>
                  <option value="desativado">Desativado</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDown size={20} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-2 mt-4">
              <button onClick={clearFilters} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                Limpar
              </button>
              <button onClick={applyFilters} className="px-4 py-2 text-sm font-medium text-white bg-[#EE4D2D] rounded-lg hover:bg-[#D03F2A]">
                <Filter size={16} className="inline-block mr-1" /> Aplicar Filtros
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-grow">
        {renderContent()}
      </div>

      {!loading && !error && users.length > 0 && totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 py-4">
          <button
            onClick={() => setCurrentPage(p => p - 1)}
            disabled={currentPage <= 1}
            className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={24} />
          </button>
          <span className="mx-4 text-sm font-medium text-gray-700">
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={currentPage >= totalPages}
            className="p-2 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}

      {isAddModalVisible && (
        <AddUserModal
          currentUser={currentUser}
          onClose={() => setIsAddModalVisible(false)}
          onSave={() => {
            setIsAddModalVisible(false);
            alert('Usuário cadastrado com sucesso!'); // MENSAGEM DE SUCESSO
            fetchUsers(1);
          }}
        />
      )}

      <EditUserModal
        user={editingUser}
        currentUser={currentUser}
        onClose={() => setEditingUser(null)}
        onSave={() => {
          setEditingUser(null);
          fetchUsers(currentPage);
        }}
      />
    </div>
  );
}